/** @jsx jsx */
import React, {
  useContext,
  Ref,
  FunctionComponent,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import ReactDOM from 'react-dom';
import {
  css,
  jsx,
  SlideOutToRight,
  SlideInFromRight,
  Icon,
  Container,
  FlexItem,
  Flex,
  Spinner,
  styled,
} from '@erecruit/ui-kit';
import { useHistory } from 'react-router-dom';

export const InsightContext = React.createContext<Ref<HTMLDivElement>>(null);

export const InsightSpinner = styled(({ className }) => (
  <Spinner
    className={className}
    size="large"
    spinning
    delay={250}
    tip="Loading..."
  />
))({
  position: 'absolute',
  margin: '-16px 0 0 -16px',
  top: '50%',
  left: '50%',
});

interface InsightPortalProps {
  children?(
    isFullScreen: boolean,
    onClose: () => void,
    onFullScreenToggle: () => void,
  ): ReactNode;
}

const InsightPortal = ({ children }: InsightPortalProps) => {
  const insightRef = useContext(InsightContext);
  const [container, setContainer] = useState();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const [shouldRender, setRender] = useState(true);

  useEffect(() => {
    if (typeof insightRef === 'object' && insightRef && insightRef.current) {
      setContainer(insightRef.current);
    }
  }, [insightRef]);

  const onClose = () => {
    setShouldClose(true);
  };

  const onFullScreenToggle = () => {
    setIsFullScreen(!isFullScreen);
  };

  if (container) {
    return ReactDOM.createPortal(
      <div
        // prettier-ignore
        css={css`
          height: 100%;
          width: 100%;
          animation: 0.3s ease-out 0s 1 ${shouldClose ? SlideOutToRight : SlideInFromRight};
          transition: padding 0.1s ease-out;
        `}
        style={isFullScreen ? undefined : { paddingLeft: '35%', minWidth: 800 }}
        onAnimationEnd={() => {
          if (shouldClose) {
            setRender(false);
          }
        }}
      >
        {(shouldRender &&
          children &&
          children(isFullScreen, onClose, onFullScreenToggle)) ||
          null}
      </div>,
      container,
    );
  }
  return null;
};

export const InsightContainer: FunctionComponent = () => {
  const insightRef = useContext(InsightContext);
  return <div style={{ height: '100%', width: '100%' }} ref={insightRef} />;
};

let closeAnimationTimeout: NodeJS.Timeout;

const Insight = ({
  children,
  referer,
}: {
  children: ReactNode;
  referer: string;
}) => {
  const history = useHistory();

  const handleBack = () => {
    history.push(referer);
    clearTimeout(closeAnimationTimeout);
  };
  return (
    <InsightPortal>
      {(isFullScreen, onClose, onFullScreenToggle) => (
        <Container
          css={{
            pointerEvents: 'all',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0px 10px 24px rgba(18, 51, 95, 0.12)',
            paddingBottom: 50,
          }}
          background="pageSecondary"
          stretch
        >
          <Flex gutter={3} padding={4}>
            <FlexItem flex="none">
              <Icon
                type="close"
                onClick={() => {
                  onClose();
                  closeAnimationTimeout = setTimeout(() => handleBack(), 300);
                }}
              />
            </FlexItem>
            <FlexItem>
              <Icon
                type="fullscreen"
                size="medium"
                style={{ verticalAlign: '-6px' }}
                onClick={() => {
                  onFullScreenToggle();
                }}
              />
            </FlexItem>
          </Flex>
          {React.Children.map(
            React.Children.toArray(children) as React.ReactElement[],
            (child: React.ReactElement) =>
              React.cloneElement(child, { isInsight: true }),
          )}
        </Container>
      )}
    </InsightPortal>
  );
};

export default Insight;
